import React, { useState, useRef, useEffect } from 'react';
import { sendChatMessage, FIRE_POS_SYSTEM_PROMPT } from '../services/swarmClient';
import { getAiMode, getSwarmEnabled } from '../services/aiRouting';
import { askLocalOllama } from '../services/ollamaClient';
import { hasAiConsent, grantAiConsent, revokeAiConsent, AiConsentError } from '../services/aiConsent';
import { buildHeaders, apiFetch } from '../lib/api';

// Quick action buttons for common questions
const QUICK_ACTIONS = [
    { id: 'wine-steak', label: '🍷 Wine for Steak', query: 'What wine pairs best with our steaks?' },
    { id: 'gluten-free', label: '🌾 Gluten-Free', query: 'What are all the gluten-free options on our menu?' },
    { id: 'chef-rec', label: '👨‍🍳 Chef Picks', query: "What are the chef's recommendations tonight?" },
    { id: 'dessert', label: '🍰 Dessert Pairing', query: 'What dessert goes well after a steak dinner?' },
    { id: 'allergies', label: '⚠️ Common Allergies', query: 'Which items contain shellfish or tree nuts?' },
    { id: 'temps', label: '🔥 Steak Temps', query: 'Explain the steak temperatures from rare to well done' },
];

// Suggested follow-up questions based on context
const FOLLOW_UPS = {
    wine: ['What about a lighter red?', 'Any wines by the glass?', 'What\'s our best selling wine?'],
    steak: ['What\'s the most popular cut?', 'Cooking times for each temp?', 'What sides pair best?'],
    seafood: ['Is it sustainable?', 'How is it prepared?', 'Wine pairing for seafood?'],
    dessert: ['Can it be shared?', 'Any dairy-free options?', 'Which desserts can be shared?'],
    allergy: ['Is cross-contamination possible?', 'Can the kitchen accommodate?', 'Alternative suggestions?'],
};

export default function EmberModal({ isOpen, onClose }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [suggestedFollowUps, setSuggestedFollowUps] = useState([]);
    const [isListening, setIsListening] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [voiceEnabled, setVoiceEnabled] = useState(() => {
        return localStorage.getItem('ember_voice_enabled') !== 'false';
    });
    // 5.1.1(i): questions leave the device (hosted AI gateway), so the chat
    // is gated on one-time explicit consent, persisted per install.
    const [aiConsent, setAiConsent] = useState(() => {
        return hasAiConsent();
    });
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const consentPanelRef = useRef(null);
    const recognitionRef = useRef(null);
    const swarmEnabled = getSwarmEnabled();

    const hasSpeechRecognition = 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window;

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Move focus into the consent panel when it appears (open, or after withdrawal).
    useEffect(() => {
        if (isOpen && !aiConsent) consentPanelRef.current?.focus();
    }, [isOpen, aiConsent]);

    // Reset when modal opens; cleanup when modal closes
    useEffect(() => {
        if (isOpen) {
            setMessages([]);
            setInput('');
            setSuggestedFollowUps([]);
        } else {
            if (window.speechSynthesis) window.speechSynthesis.cancel();
            if (recognitionRef.current) recognitionRef.current.abort();
            setIsSpeaking(false);
            setIsListening(false);
        }
    }, [isOpen]);

    const startListening = () => {
        if (!hasSpeechRecognition || isListening) return;
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = 'en-US';

        recognition.onresult = (e) => {
            const transcript = e.results[0][0].transcript;
            setInput(transcript);
            setIsListening(false);
            // Auto-submit after a brief delay
            setTimeout(() => handleSend(transcript), 100);
        };

        recognition.onerror = () => setIsListening(false);
        recognition.onend = () => setIsListening(false);

        recognitionRef.current = recognition;
        recognition.start();
        setIsListening(true);
    };

    const speak = (text) => {
        if (!voiceEnabled || !window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        const voices = window.speechSynthesis.getVoices();
        const enVoice = voices.find(v => v.lang.startsWith('en'));
        if (enVoice) utterance.voice = enVoice;
        utterance.rate = 1.1;
        utterance.pitch = 1.0;
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
    };

    const toggleVoice = () => {
        const newVal = !voiceEnabled;
        setVoiceEnabled(newVal);
        localStorage.setItem('ember_voice_enabled', String(newVal));
        if (!newVal && window.speechSynthesis) {
            window.speechSynthesis.cancel();
            setIsSpeaking(false);
        }
    };

    const tryOperationalCommand = async (text) => {
        const lower = text.toLowerCase().trim();

        // "fire table N" command
        const fireMatch = lower.match(/fire\s+table\s+(\d+)/);
        if (fireMatch) {
            const tableNum = fireMatch[1];
            try {
                const res = await apiFetch(`/api/orders/table/${tableNum}/fire-all`, { method: 'POST' });
                const data = await res.json();
                return data.success
                    ? `✅ Table ${tableNum} has been fired! Orders are now cooking.`
                    : `❌ Could not fire Table ${tableNum}: ${data.error || 'Unknown error'}`;
            } catch {
                return `❌ Failed to fire Table ${tableNum} — server error`;
            }
        }

        // "86 [item]" command — fuzzy match not needed, just acknowledge
        const eightyMatch = lower.match(/(?:86|eighty.?six)\s+(.+)/);
        if (eightyMatch) {
            const itemName = eightyMatch[1];
            return `⚠️ To 86 "${itemName}", use the Menu Management screen or the item's 86 toggle. Ember can't directly 86 items by name (for safety).`;
        }

        return null; // not a command
    };

    const detectContext = (message) => {
        const lower = message.toLowerCase();
        if (lower.includes('wine') || lower.includes('cabernet') || lower.includes('pinot')) return 'wine';
        if (lower.includes('steak') || lower.includes('ribeye') || lower.includes('filet')) return 'steak';
        if (lower.includes('seafood') || lower.includes('lobster') || lower.includes('fish')) return 'seafood';
        if (lower.includes('dessert') || lower.includes('cake') || lower.includes('sweet')) return 'dessert';
        if (lower.includes('allerg') || lower.includes('gluten') || lower.includes('dairy')) return 'allergy';
        return null;
    };

    const allowAi = () => {
        grantAiConsent();
        setAiConsent(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const withdrawAi = () => {
        revokeAiConsent();
        setAiConsent(false);
        setMessages([]);
        setSuggestedFollowUps([]);
    };

    const handleSend = async (messageText = null) => {
        // Every send path funnels through here (typed, Enter, voice
        // transcript, quick actions, follow-ups) — consent gates them all.
        if (!aiConsent || !hasAiConsent()) return;
        const textToSend = messageText || input;
        if (!textToSend.trim()) return;

        // Check for operational commands first
        const commandResult = await tryOperationalCommand(textToSend);
        if (commandResult) {
            setMessages(prev => [...prev, { role: 'user', content: textToSend }]);
            setInput('');
            const botMsg = { role: 'assistant', content: commandResult };
            setMessages(prev => [...prev, botMsg]);
            if (voiceEnabled) speak(commandResult);
            return;
        }

        const userMessage = { role: 'user', content: textToSend };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);
        setSuggestedFollowUps([]);

        const runRag = async () => {
            if (!hasAiConsent()) throw new AiConsentError();
            const response = await apiFetch(`/api/rag/chat`, {
                method: 'POST',
                headers: buildHeaders(),
                body: JSON.stringify({
                    message: userMessage.content,
                    history: messages.slice(-5)
                })
            });

            const data = await response.json();

            if (data.error) throw new Error(data.error);

            const ragContent = data.message || data.response;
            // No silent substitution: an empty RAG reply is a failure the
            // caller's catch chain handles with an honestly-labelled answer.
            if (!ragContent) throw new Error('RAG response missing');
            return {
                role: 'assistant',
                content: ragContent,
                origin: 'hosted',
                sources: data.sources
            };
        };

        const runSwarm = async () => {
            if (!swarmEnabled) {
                throw new Error('Swarm disabled');
            }
            const swarmResponse = await sendChatMessage(
                [...messages.slice(-5), userMessage],
                FIRE_POS_SYSTEM_PROMPT
            );
            const swarmContent = swarmResponse?.message
                || swarmResponse?.response
                || swarmResponse?.data?.message
                || swarmResponse?.data?.response
                || swarmResponse?.result
                || swarmResponse?.answer;

            if (swarmResponse?.success === false || !swarmContent) {
                throw new Error(swarmResponse?.error || 'Swarm response missing');
            }

            return {
                role: 'assistant',
                content: swarmContent,
                origin: 'hosted'
            };
        };

        const finalizeReply = (botMessage) => {
            setMessages(prev => [...prev, botMessage]);
            if (voiceEnabled) speak(botMessage.content);
            const context = detectContext(textToSend + ' ' + botMessage.content);
            if (context && FOLLOW_UPS[context]) {
                setSuggestedFollowUps(FOLLOW_UPS[context]);
            }
        };

        const runLocalOllama = async () => ({
            role: 'assistant',
            content: await askLocalOllama([...messages.slice(-5), userMessage]),
            origin: 'hosted'
        });

        const aiMode = getAiMode();

        // Rufus uses the authenticated gateway proxy on every shipped platform.
        // The service name is retained for compatibility with older callers.
        const primary = runLocalOllama;
        const fallbackFn = runRag;

        try {
            if (aiMode === 'local') {
                finalizeReply({
                    role: 'assistant',
                    content: getLocalResponse(),
                    origin: 'builtin'
                });
                return;
            }

            const botMessage = await primary();
            finalizeReply(botMessage);
        } catch (error) {
            console.error('Ember Error:', error);

            try {
                const fallbackMessage = await (aiMode === 'local' ? null : fallbackFn());
                if (fallbackMessage) {
                    finalizeReply(fallbackMessage);
                    return;
                }
            } catch (fallbackError) {
                console.error('Ember Fallback Error:', fallbackError);
            }

            finalizeReply({
                role: 'assistant',
                content: getLocalResponse(),
                origin: 'builtin'
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Shown when the hosted assistant cannot be reached. Deliberately generic:
    // every restaurant has its own menu, so nothing menu-specific lives in the app.
    const getLocalResponse = () =>
        "I can't reach the assistant right now, so I can't look at the menu for you.\n\n" +
        'In the meantime:\n' +
        '• Open the item in the order screen to see its description, allergens and options\n' +
        '• For allergies, always confirm with the kitchen before the order is sent\n' +
        '• Ask your manager about specials or anything that is 86\'d\n\n' +
        'Please try your question again in a moment.';

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleQuickAction = (action) => {
        handleSend(action.query);
    };

    const handleFollowUp = (question) => {
        handleSend(question);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 animate-fade-in" role="dialog" aria-modal="true" aria-label="Ask Ember AI Assistant" onClick={onClose}>
            <div
                className="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl h-[700px] flex flex-col border-2 border-purple-500 animate-scale-in overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-700 to-purple-600 text-white p-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-xl">
                            🤖
                        </div>
                        <div>
                            <h2 className="text-xl font-bold">Ask Ember</h2>
                            <p className="text-sm text-purple-200">AI Sommelier & Menu Expert</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close Ember AI assistant"
                        className="text-white hover:bg-purple-800 rounded-full w-10 h-10 flex items-center justify-center text-2xl transition-colors focus:outline-none focus:ring-2 focus:ring-white"
                    >
                        <span aria-hidden="true">×</span>
                    </button>
                </div>

                {/* Quick Actions — hidden until AI consent so no visible
                    control silently no-ops against the handleSend guard. */}
                {aiConsent && messages.length === 0 && (
                    <div className="p-4 border-b border-gray-700">
                        <p className="text-gray-400 text-sm mb-3">Quick Questions:</p>
                        <div className="flex flex-wrap gap-2">
                            {QUICK_ACTIONS.map(action => (
                                <button
                                    key={action.id}
                                    onClick={() => handleQuickAction(action)}
                                    className="bg-purple-900/50 hover:bg-purple-800 text-purple-200 text-sm px-3 py-2 rounded-full transition-colors border border-purple-700"
                                >
                                    {action.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Messages */}
                <div className="flex-1 overflow-auto p-4 space-y-4" role="log" aria-label="Conversation with Ember" aria-live="polite">
                    {messages.length === 0 && (
                        <div className="text-gray-400 text-center mt-8">
                            <div className="text-5xl mb-4">👋</div>
                            <p className="text-lg mb-2">Hi! I'm Ember, your AI assistant.</p>
                            <p className="text-sm text-gray-500">
                                Ask me about wine pairings, ingredients, allergies, or cooking recommendations.
                            </p>
                        </div>
                    )}

                    {messages.map((msg, idx) => (
                        <div
                            key={idx}
                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                            <div className={`max-w-[85%] rounded-2xl p-4 ${
                                msg.role === 'user'
                                    ? 'bg-purple-600 text-white rounded-br-md'
                                    : 'bg-gray-800 text-gray-100 rounded-bl-md'
                            }`}>
                                <p className="whitespace-pre-wrap text-sm leading-relaxed">
                                    {msg.content}
                                </p>
                                {msg.sources && msg.sources.length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-gray-700 text-xs text-gray-400">
                                        Sources: {msg.sources.map(s => s.metadata?.source || 'Menu').join(', ')}
                                    </div>
                                )}
                                {msg.origin === 'builtin' && (
                                    <div className="mt-2 pt-2 border-t border-gray-700 text-xs text-amber-400">
                                        The assistant is unavailable right now. This is a generic reply, not from your menu.
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    {isLoading && (
                        <div className="flex justify-start">
                            <div className="bg-gray-800 text-gray-100 rounded-2xl rounded-bl-md p-4">
                                <div className="flex items-center gap-2">
                                    <div className="flex gap-1">
                                        <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                        <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                        <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                    </div>
                                    <span className="text-gray-400 text-sm">Thinking...</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {/* Suggested Follow-ups */}
                {suggestedFollowUps.length > 0 && !isLoading && (
                    <div className="px-4 py-2 border-t border-gray-700">
                        <div className="flex flex-wrap gap-2">
                            {suggestedFollowUps.map((question, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => handleFollowUp(question)}
                                    className="bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs px-3 py-1.5 rounded-full transition-colors"
                                >
                                    {question}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Speaking indicator */}
                {isSpeaking && (
                    <div className="px-4 py-1 text-xs text-purple-300 flex items-center gap-2">
                        <span className="animate-pulse">🔊</span> Speaking...
                    </div>
                )}

                {/* Input */}
                <div className="p-4 bg-gray-800 border-t border-gray-700">
                    {aiConsent ? (<>
                    <div className="flex gap-2">
                        <label htmlFor="ember-input" className="sr-only">Ask Ember a question</label>
                        <input
                            id="ember-input"
                            ref={inputRef}
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Ask about wine, ingredients, allergies..."
                            className="flex-1 bg-gray-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 placeholder-gray-400"
                            disabled={isLoading}
                        />
                        {/* Mic button */}
                        {hasSpeechRecognition && (
                            <button
                                onClick={startListening}
                                disabled={isLoading || isListening}
                                aria-label={isListening ? 'Listening...' : 'Start voice input'}
                                className={`px-4 py-3 rounded-xl font-bold transition-all focus:outline-none focus:ring-2 focus:ring-purple-400 ${
                                    isListening
                                        ? 'bg-red-500 text-white ring-4 ring-red-400/50 animate-pulse'
                                        : 'bg-gray-700 hover:bg-gray-600 text-white'
                                }`}
                            >
                                🎤
                            </button>
                        )}
                        <button
                            onClick={() => handleSend()}
                            disabled={isLoading || !input.trim()}
                            aria-label={isLoading ? 'Thinking...' : 'Send question to Ember'}
                            className="bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 text-white px-6 py-3 rounded-xl font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-purple-400"
                        >
                            {isLoading ? '...' : 'Ask'}
                        </button>
                        <button
                            onClick={toggleVoice}
                            aria-label={voiceEnabled ? 'Disable voice responses' : 'Enable voice responses'}
                            title={voiceEnabled ? 'Voice ON - click to disable' : 'Voice OFF - click to enable'}
                            className={`px-3 py-3 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-purple-400 ${
                                voiceEnabled ? 'bg-purple-700 text-white' : 'bg-gray-700 text-gray-400'
                            }`}
                        >
                            🔊
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                        Answers are generated by Groq, Inc. through Quantrolix servers.{' '}
                        <button
                            onClick={withdrawAi}
                            className="underline hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-400"
                        >
                            Withdraw AI permission
                        </button>
                    </p>
                    </>) : (
                    <div
                        data-testid="ai-consent-panel"
                        ref={consentPanelRef}
                        tabIndex={-1}
                        role="group"
                        aria-label="Ask Ember AI permission"
                        className="text-center space-y-3 focus:outline-none"
                    >
                        <p className="text-sm text-gray-300">
                            Ask Ember sends the question you type or speak, your recent
                            messages in this chat, and the restaurant menu data to
                            Quantrolix servers, which pass them to Groq, Inc. (our AI
                            provider) to generate an answer. Do not include
                            personal information in questions. See the{' '}
                            <a href="https://apps.quantrolix.ai/privacy#fire-pos" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a>.
                            You can withdraw permission at any time.
                        </p>
                        <div className="flex gap-2 justify-center">
                            <button
                                onClick={allowAi}
                                className="bg-purple-600 hover:bg-purple-500 text-white px-6 py-3 rounded-xl font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-purple-400"
                            >
                                Allow
                            </button>
                            <button
                                onClick={onClose}
                                className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-xl font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-purple-400"
                            >
                                Don't Allow
                            </button>
                        </div>
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
}
